/**
 * ChangePasswordDialog Component
 *
 * Force le changement de mot de passe à la première connexion
 * S'affiche automatiquement si passwordChangeRequired est true
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon
} from '@mui/icons-material';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../../firebase/config';
import AdminUsersApiService from '../../services/adminUsersApi';
import { errorField } from '../../lib/http';
import { logger } from '../../lib/logger';

interface ChangePasswordDialogProps {
  open: boolean;
  onPasswordChanged: () => void;
  required?: boolean; // Si true, l'utilisateur ne peut pas fermer sans changer
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onPasswordChanged,
  required = false
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validatePassword = (): string | null => {
    if (!currentPassword) {
      return 'Veuillez entrer votre mot de passe actuel';
    }

    if (!newPassword) {
      return 'Veuillez entrer un nouveau mot de passe';
    }

    if (newPassword.length < 6) {
      return 'Le mot de passe doit contenir au moins 6 caractères';
    }

    if (newPassword === currentPassword) {
      return 'Le nouveau mot de passe doit être différent de l\'ancien';
    }

    if (newPassword !== confirmPassword) {
      return 'Les mots de passe ne correspondent pas';
    }

    return null;
  };

  const handleChangePassword = async () => {
    // Valider
    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('Utilisateur non connecté');
      }

      // Ré-authentifier l'utilisateur avec le mot de passe actuel
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Mettre à jour le mot de passe
      await updatePassword(user, newPassword);

      // Mettre à jour le flag dans Firestore via API
      try {
        await AdminUsersApiService.updateAdminUser(user.uid, {
          metadata: {
            passwordChangeRequired: false,
            passwordChangedAt: new Date().toISOString()
          }
        });
      } catch (apiError) {
        logger.error('Erreur lors de la mise à jour du flag:', apiError);
        // Ne pas bloquer si l'API échoue
      }

      // Succès
      onPasswordChanged();
    } catch (err: unknown) {
      logger.error('Erreur lors du changement de mot de passe:', err);

      if (errorField(err, 'code') === 'auth/wrong-password') {
        setError('Mot de passe actuel incorrect');
      } else if (errorField(err, 'code') === 'auth/weak-password') {
        setError('Le mot de passe est trop faible');
      } else if (errorField(err, 'code') === 'auth/requires-recent-login') {
        setError('Veuillez vous reconnecter et réessayer');
      } else {
        setError(errorField(err, 'message') || 'Erreur lors du changement de mot de passe');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!required && !loading) {
      onPasswordChanged();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={required}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LockIcon color="warning" />
          <Typography variant="h6">
            {required ? 'Changement de mot de passe requis' : 'Changer le mot de passe'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {/* Alert */}
          {required && (
            <Alert severity="warning">
              Pour des raisons de sécurité, vous devez changer votre mot de passe avant de continuer.
            </Alert>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Mot de passe actuel */}
          <TextField
            label="Mot de passe actuel"
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            fullWidth
            required
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* Nouveau mot de passe */}
          <TextField
            label="Nouveau mot de passe"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            required
            autoComplete="new-password"
            helperText="Minimum 6 caractères"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* Confirmer le mot de passe */}
          <TextField
            label="Confirmer le nouveau mot de passe"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            required
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* Conseils de sécurité */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Conseils pour un mot de passe sécurisé :
            </Typography>
            <Typography variant="caption" color="text.secondary" component="ul" sx={{ pl: 2, m: 0 }}>
              <li>Au moins 8 caractères (recommandé)</li>
              <li>Mélange de lettres majuscules et minuscules</li>
              <li>Chiffres et caractères spéciaux</li>
              <li>Évitez les mots du dictionnaire</li>
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        {!required && (
          <Button onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
        )}
        <Button
          onClick={handleChangePassword}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Changer le mot de passe
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
